import * as R from 'ramda';
import { ActionCreatorsMapObject } from 'redux';
import { createSelector, defaultMemoize } from 'reselect';
import { getStore } from 'store/store';
import { arrayHelper } from 'utils/arrayHelper';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';
import { ModuleState } from 'utils/stateHelper';
import * as Types from 'utils/Types';

import { GlobalState } from 'reactApp/core/rootReducer';

type MakeCreateActionProps<T, Item> = {
    module: ModuleState<T, GlobalState>;
    apiMethod: (...args: any[]) => Promise<Item>;
    entityName: string;
    beforeCreate?: (...args: any[]) => any | any[];
    idKey?: string;
    argumentIdGetter?: (arg: any) => any;
    loadItemOnRemoveAction?: (id: any) => Promise<void>;
    listKey?: string;
    handleError?: (error: any) => any;
    handleSuccess?: (item: Item) => Item;
    afterCreate?: (item: Item) => void;
};
export const reduxHelper = {
    createSelectorWithArguments: (...creatorArguments) => {
        const selectors = [...creatorArguments];
        const resultFunc = selectors.pop();

        const selectorResultFunc = (...selectorsResult) =>
            defaultMemoize((...args) =>
                resultFunc(...selectorsResult, ...args),
            );

        // @ts-ignore
        const selector = createSelector(
            ...selectors,
            // @ts-ignore
            selectorResultFunc,
        );

        return (state, ...args) => selector(state)(...args);
    },
    bindActions: <T extends ActionCreatorsMapObject>(actions: T) =>
        // @ts-ignore
        (R.map((action) => {
            return (...args) => getStore().dispatch(action(...args));
        }, actions) as any) as {
            [P in keyof T]: Types.CutMiddleFunction<T[P]>;
        },
    bindSelectors: <S extends Types.StringMap>(selectors: S) =>
        // @ts-ignore
        R.map((selector) => {
            return (...args) => selector(getStore().getState(), ...args);
        }, selectors) as Types.RemoveFirstArgumentOfObjectProps<S>,
    bindModule: ({ actions, selectors }) => {
        return {
            actions: reduxHelper.bindActions(actions),
            selectors: reduxHelper.bindSelectors(selectors),
        };
    },
    subscribeOnSelector({ onChange, boundSelector }) {
        let currentState;

        function handleChange() {
            const nextState = boundSelector();
            if (nextState !== currentState) {
                currentState = nextState;
                onChange(currentState);
            }
        }

        const unsubscribe = getStore().subscribe(handleChange);
        handleChange();
        return unsubscribe;
    },

    createLoadListAction<T extends Types.BasicModuleState, List>({
        module,
        apiMethod,
        entityName,
        fieldKey = 'list',
    }: {
        module: ModuleState<T, GlobalState>;
        apiMethod: (...args: any[]) => Promise<List>;
        entityName: string;
        fieldKey?: string;
    }) {
        return (...args) => async (dispatch) => {
            dispatch(
                // @ts-ignore
                module.mergeModuleState({
                    isLoaded: false,
                    isLoading: true,
                    hasError: false,
                }),
            );

            try {
                const data = await apiMethod(...args);

                dispatch(
                    // @ts-ignore
                    module.mergeModuleState({
                        [fieldKey]: data,
                        isLoaded: true,
                        isLoading: false,
                        hasError: false,
                    }),
                );

                return data;
            } catch (error) {
                dispatch(
                    // @ts-ignore
                    module.mergeModuleState({
                        hasError: true,
                        isLoaded: false,
                        isLoading: false,
                    }),
                );

                console.error(`${entityName} ${fieldKey} loading error`, error);

                throw error;
            }
        };
    },

    createLoadListSavingStateAction({ loadList, updateList, module }) {
        return (...args) => async (dispatch, getState) => {
            if (
                module.getPart(getState(), 'isLoaded') &&
                !module.getPart(getState(), 'hasError')
            ) {
                return dispatch(updateList(...args));
            }
            return dispatch(loadList(...args));
        };
    },

    createLoadListSavingStateActionOnly<
        T extends Types.BasicModuleState,
        List
    >({
        module,
        apiMethod,
        entityName,
        fieldKey = 'list',
        entityNamePublic = '',
    }: {
        module: ModuleState<T, GlobalState>;
        apiMethod: (...args: any[]) => Promise<List>;
        entityName: string;
        fieldKey?: string;
        entityNamePublic?: string;
    }) {
        const initialLoadListAction = reduxHelper.createLoadListAction({
            module,
            apiMethod,
            entityName,
            fieldKey,
        });

        const updateListAction = reduxHelper.createUpdateListAction({
            module,
            apiMethod,
            entityName,
            entityNameLocal: entityNamePublic,
            fieldKey,
        });

        return reduxHelper.createLoadListSavingStateAction({
            loadList: initialLoadListAction,
            updateList: updateListAction,
            module,
        });
    },

    createLoadListIfNotLoadedAction({ loadList, module, fieldKey = 'list' }) {
        return (...args) => async (dispatch, getState) => {
            if (!module.getPart(getState(), 'isLoaded')) {
                await dispatch(loadList(...args));
            }

            return module.getPart(getState(), fieldKey);
        };
    },

    createLoadItemAction({
        module,
        apiMethod,
        entityName,
        entityNamePublic,
        idKey = 'id',
        listFieldKey = 'list',
    }) {
        return (id) => async (dispatch, getState) => {
            try {
                const item = await apiMethod(id);

                dispatch(
                    module.mergeModuleState({
                        [listFieldKey]: arrayHelper.getListWithInsertedItem({
                            item,
                            list: module.getPart(getState(), listFieldKey),
                            idKey,
                        }),
                    }),
                );

                return item;
            } catch (error) {
                console.error(`${entityName} loading error`, error);
                throw `Не удалось загрузить ${entityNamePublic} ${id}`;
            }
        };
    },

    createUpdateListAction({
        module,
        apiMethod,
        entityName,
        entityNameLocal,
        fieldKey = 'list',
    }) {
        return (...args) => async (dispatch) => {
            try {
                const data = await apiMethod(...args);

                dispatch(
                    module.mergeModuleState({
                        [fieldKey]: data,
                    }),
                );
                return data;
            } catch (error) {
                console.error(`${entityName} loading error`, error);
                throw `Не удалось загрузить ${entityNameLocal}`;
            }
        };
    },

    createEntityListLoader: ({ apiMethod, modulePropName, module }) => (
        ...args
    ) => async (dispatch) => {
        let entityList;

        try {
            entityList = await apiMethod(...args);
        } catch (error) {
            console.error(`${modulePropName} loading error`, error);
            throw error;
        }

        dispatch(module.mergeModuleState({ [modulePropName]: entityList }));

        return entityList;
    },

    makeCreateAction<T extends Types.BasicModuleState, Item>({
        module,
        apiMethod,
        entityName,
        beforeCreate,
        handleError,
        handleSuccess,
        afterCreate,
        idKey = 'id',
        listKey = 'list',
    }: MakeCreateActionProps<T, Item>) {
        return (...args) => async (dispatch, getState) => {
            let handledArgs = args;
            if (beforeCreate) {
                handledArgs = beforeCreate(...args);
                // @ts-ignore
                handledArgs = [].concat(handledArgs);
            }

            let item;
            try {
                item = await apiMethod(...handledArgs);
            } catch (error) {
                console.error(`can't create ${entityName}`, error);
                if (handleError) {
                    return handleError(error);
                }
                throw error;
            }

            const { notCallAfterCreate = false } = args[0];
            if (!notCallAfterCreate && afterCreate) {
                afterCreate(item);
            }

            if (handleSuccess) {
                return dispatch(handleSuccess(item));
            }
            dispatch(
                module.setPart(
                    listKey as any,
                    arrayHelper.getListWithInsertedItem({
                        list: module.getPart(getState(), listKey as any),
                        item,
                        idKey,
                    }),
                ),
            );

            return item;
        };
    },

    makeRemoveAction<T extends Types.BasicModuleState>({
        module,
        apiMethod,
        entityName,
        entityNamePublic,
        beforeRemove,
        idKey = 'id',
        argumentIdGetter = R.identity,
        loadItemOnRemoveAction,
        listKey = 'list',
    }: {
        module: ModuleState<T, GlobalState>;
        apiMethod: (...args: any[]) => Promise<void>;
        entityName: string;
        entityNamePublic: string;
        beforeRemove?: (...args: any[]) => void;
        idKey?: string;
        argumentIdGetter?: (arg: any) => any;
        loadItemOnRemoveAction?: (
            id: any,
        ) => (dispatch: any, getState: any) => Promise<void>;
        listKey?: string;
    }) {
        return (...args) => async (dispatch, getState) => {
            if (beforeRemove) {
                beforeRemove(...args);
            }

            try {
                await apiMethod(...args);
            } catch (error) {
                console.error(`can't remove ${entityName}`, error);
                throw `Не удалось удалить ${entityNamePublic}`;
            }

            const id = argumentIdGetter(args[0]);

            if (loadItemOnRemoveAction) {
                await dispatch(loadItemOnRemoveAction(id));
            } else {
                dispatch(
                    module.setPart(
                        listKey as any,
                        // @ts-ignore
                        arrayHelper.getListWithoutItem({
                            list: module.getPart(getState(), listKey as any),
                            id,
                            idKey,
                        }),
                    ),
                );
            }
        };
    },

    makeEditAction<
        Item,
        UpdateItemData = Partial<Item>,
        M extends ModuleState<any, any> = ModuleState<any, any>
    >({
        module,
        apiMethod,
        idKey = 'id',
        listKey = 'list',
        onItemEdited = R.identity,
    }: {
        module: M;
        apiMethod: (...args: any[]) => Promise<Item>;
        idKey?: string;
        listKey?: string;
        onItemEdited?: (...args: any[]) => any;
    }) {
        return (
            id: any,
            data: UpdateItemData,
            ...args: any[]
        ): ReduxTypeUtils.ThunkResult<Promise<Item>> => async (
            dispatch,
            getState,
        ) => {
            const item = await apiMethod(id, data, ...args);

            dispatch(
                module.setPart(
                    listKey,
                    arrayHelper.getListWithInsertedItem({
                        list: module.getPart(getState(), listKey),
                        idKey,
                        item,
                    }),
                ),
            );

            await dispatch(onItemEdited());

            return item;
        };
    },

    multiplyAction(action) {
        return (ids, ...args) => async (dispatch) => {
            const failedIds: any[] = [];
            const failedErrors: any[] = [];

            await Promise.all(
                ids.map(async (id) => {
                    try {
                        await dispatch(action(id, ...args));
                    } catch (error) {
                        failedIds.push(id);
                        failedErrors.push(error);
                    }
                }),
            );

            if (failedIds.length) {
                throw failedIds;
            }
        };
    },

    makeStandardSelectors<Item>(
        module,
        { idKey = 'id', listKey = 'list' } = {},
    ) {
        const getList = (state: GlobalState) =>
            module.getPart(state, listKey) as Item[];
        const getItem = (state: GlobalState, id) =>
            getList(state).find((item) => item[idKey] === id);
        const getIsLoading = (state: GlobalState) =>
            module.getPart(state, 'isLoading') as boolean;
        const getIsLoaded = (state: GlobalState) =>
            module.getPart(state, 'isLoaded') as boolean;
        const getHasError = (state: GlobalState) =>
            module.getPart(state, 'hasError') as boolean;

        return {
            getList,
            getItem,
            getIsLoading,
            getIsLoaded,
            getHasError,
        };
    },

    createObjItemGetter: (listGetter) => (state, id) => {
        // @ts-ignore
        return R.pipe(
            listGetter,
            R.prop(id),
            // @ts-ignore
        )(state);
    },

    createParallelActionsCaller: (actions: Types.AnyFunction[]) => () => async (
        dispatch,
    ) => {
        return Promise.all(actions.map((action) => dispatch(action())));
    },
};
